import { useEffect, useRef } from 'react';
import type { AppConfig } from '../config';
import { env } from '../config/env';
import { warmCameraKit } from '../ar';
import { warmMouthDetector } from '../ar/mouthOpenDetector';
import { warmLogo } from '../capture';
import { DevHud } from '../components/DevHud';
import { CaptureScreen } from '../screens/CaptureScreen';
import { EndCardScreen } from '../screens/EndCardScreen';
import { ErrorScreen } from '../screens/ErrorScreen';
import { InAppBrowserScreen } from '../screens/InAppBrowserScreen';
import { InstructionScreen } from '../screens/InstructionScreen';
import { PermissionScreen } from '../screens/PermissionScreen';
import { ResultScreen } from '../screens/ResultScreen';
import { TitleScreen } from '../screens/TitleScreen';
import { detectBrowserContext } from '../lib/inAppBrowser';
import { captureAttribution } from '../lib/utm';
import { uploadClipInBackground } from '../upload';
import { useActivation } from './useActivation';
import { devWarn } from '../lib/devLog';

/**
 * Web sequence:
 *   [in-app browser] → title (+ permissions) → instruction → capture → result → end
 *
 * The permission ask lives on the title screen, per the storyboard. `permission`
 * survives as a step only for the refusal, which needs a screen of its own.
 *
 * The in-app-browser check runs before the title rather than after it: a webview
 * may not be able to grant a camera at all, so warning once the prompt has
 * already been refused is too late to be useful.
 *
 * The instruction clip runs on every path, Camera Kit included. It used to be
 * skipped there so the grant landed straight on live Lens preview, which was the
 * right call while the clip was an empty placeholder slot — there was nothing to
 * skip. Now that the siblings' clip is delivered it is the thing that teaches the
 * caw, and the shortcut was cutting web guests out of it entirely.
 *
 * It also buys time rather than costing it: WASM + Lens warm from the title
 * screen, and the clip gives that warm another twenty seconds to finish before
 * capture asks for a session. A guest who does not want to wait has the "I am
 * ready" button.
 */
export function WebFlow({ config }: { config: AppConfig }) {
  const a = useActivation(config, 'title');
  const uploadedRef = useRef<string | null>(null);
  const cameraKit = env.arModule === 'camera-kit';

  // UTM capture on land, before any navigation can lose the query string.
  useEffect(() => {
    captureAttribution();
  }, []);

  // Browser check on land, before the title screen can ask for a camera.
  useEffect(() => {
    if (!config.checkInAppBrowser) return;
    if (detectBrowserContext().shouldWarn) a.goTo('inAppBrowser');
    // Once, on mount — a later re-run would yank a guest who chose to continue
    // back to the warning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.checkInAppBrowser]);

  // Warm Camera Kit (Snap CDN) + local MediaPipe while on the title screen.
  useEffect(() => {
    if (!cameraKit) return;
    void warmCameraKit().catch((err) => {
      devWarn('[web] Camera Kit warm failed — will retry at capture', err);
    });
    // ~630KB and a 1.5M-pixel knockout pass; pay for it here, not at the
    // trigger. Cached for the life of the page.
    void warmLogo();
    void warmMouthDetector().catch((err) => {
      devWarn('[web] MediaPipe warm failed — will retry at capture', err);
    });
  }, [cameraKit]);

  /**
   * Fire-and-forget upload, deliberately in an effect keyed on the result step:
   * it cannot start until the clip is already on the guest's screen. No queue,
   * no retry, no failure state. Sampled and switchable via config.
   */
  useEffect(() => {
    if (a.step !== 'result' || !a.clip) return;
    if (!config.backgroundUpload) return;
    if (uploadedRef.current === a.sessionId) return;
    uploadedRef.current = a.sessionId;
    uploadClipInBackground({
      sessionId: a.sessionId,
      blob: a.clip.blob,
      mimeType: a.clip.mimeType,
    });
  }, [a.clip, a.sessionId, a.step, config.backgroundUpload]);

  return (
    <>
      <DevHud mode="web" step={a.step} />

      {a.step === 'title' ? (
        <TitleScreen
          onStart={(result) => {
            a.setMic(result?.micStream ?? null, result?.micGranted ?? false);
            a.setCameraStream(result?.cameraStream ?? null);
            a.goTo('instruction');
          }}
          onDenied={() => a.goTo('permission')}
        />
      ) : null}

      {a.step === 'inAppBrowser' ? <InAppBrowserScreen onContinue={() => a.goTo('title')} /> : null}

      {a.step === 'permission' ? <PermissionScreen /> : null}

      {a.step === 'instruction' ? (
        <InstructionScreen config={config} onDone={() => a.goTo('capture')} />
      ) : null}

      {a.step === 'capture' ? (
        <CaptureScreen
          config={config}
          micStream={a.micStream}
          cameraStream={a.cameraStream}
          onCaptured={(clip) => {
            a.acceptClip(clip);
            a.completeSession();
            a.goTo('result');
          }}
          onFailed={a.fail}
        />
      ) : null}

      {a.step === 'result' ? (
        <ResultScreen
          config={config}
          clip={a.clip}
          clipUrl={a.clipUrl}
          micGranted={a.micGranted}
          onRetake={a.retake}
          onNext={() => a.goTo('endCard')}
        />
      ) : null}

      {a.step === 'endCard' ? (
        <EndCardScreen config={config} onRestart={() => a.resetSession('title')} />
      ) : null}

      {a.step === 'error' ? (
        <ErrorScreen config={config} error={a.error} onRetry={() => a.goTo('capture')} />
      ) : null}
    </>
  );
}
